import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Tag, Search, Trash2 } from 'lucide-react';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import {
  sliceQawaemStats,
  QAWAEM_KPI_GRID_CLASS,
} from '../../products/saudi/components/QawaemErpListPageShell';
import localApiAdapter from '../../core/services/localApiAdapter';
import { HR_EMPLOYEES_REFRESH_EVENT, useHrApiRefreshListener } from '../ressources-humaines/hrRefreshEvents';

export {
  AppNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
};

export { sliceQawaemStats, QAWAEM_KPI_GRID_CLASS };

/** Pointage entity reference defaults (settings.configuration.entityPrefixes). */
export const POINTAGE_ENTITY_REF_DEFAULTS = {
  timeEntry: { prefixKey: 'timeEntry', fallback: 'PNT', field: 'entry_ref' },
  overtime: { prefixKey: 'overtime', fallback: 'OVT', field: 'overtime_ref' },
  schedule: { prefixKey: 'schedule', fallback: 'HOR', field: 'schedule_code', apiField: 'ref' },
};

/** Next entity reference (رمز) for pointage CRUD pages. */
export function nextPointageEntityRef(settings, prefixKey, fallback, records, getRef) {
  const prefix = getEntityPrefix(settings, prefixKey, fallback);
  return generateEntityReference(prefix, records, getRef);
}

/** Hook: stable callback to generate the next reference for a pointage entity list. */
export function usePointageEntityReference(prefixKey, fallback, records, refField) {
  const { settings } = useSettings();
  return useCallback(
    () => nextPointageEntityRef(settings, prefixKey, fallback, records, (r) => r[refField]),
    [settings, prefixKey, fallback, records, refField],
  );
}

/** Bottom-placed CRUD toast — same shell as Products.js / Facturation list pages. */
export function PointageCrudNotificationToast({ toast, onDismiss }) {
  return (
    <AppNotificationToast
      visible={toast.visible}
      message={toast.message}
      messageHtml={toast.messageHtml}
      variant={toast.variant}
      status={toast.status}
      showActions={toast.showActions}
      topLabel={toast.topLabel}
      onDismiss={onDismiss}
      placement="bottom"
    />
  );
}

/** Qawaem actions column header — matches Products.js align-middle. */
export const POINTAGE_ACTIONS_TH_CLASS =
  'px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle';

/** Bulk-delete chip for FacturationListPageLayout `filterActions` (Products pattern). */
export function buildPointageBulkDeleteFilterAction({
  count,
  onDelete,
  label,
  title,
}) {
  if (!count || count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onDelete}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
      title={title}
    >
      <Trash2 size={16} />
      <span>{label}</span>
    </button>
  );
}

/** Map /hr/employees API rows to CustomSelect options (live HR interconnection). */
export function mapHrEmployeesToSelectOptions(list, { valueField = 'name' } = {}) {
  const employees = Array.isArray(list) ? list : (list?.data || []);
  return employees.map((emp) => {
    const fullName =
      `${emp.prenom || emp.firstName || ''} ${emp.nom || emp.lastName || ''}`.trim() ||
      emp.matricule ||
      emp.mat ||
      String(emp.id);
    const empId = String(emp.id);
    const mat = emp.mat || emp.matricule || empId;
    const value = valueField === 'id' ? empId : fullName;
    return {
      value,
      label: `${mat} — ${fullName}`,
      meta: {
        id: emp.id,
        matricule: mat,
        name: fullName,
        salary: Number(emp.salary ?? emp.salaire_base ?? emp.salaireBase ?? emp.salaire_net ?? 0),
      },
    };
  });
}

/** Fetch active employees from HR for pointage entity pickers. */
export async function fetchPointageEmployeeOptions(apiGet, { status = 'active', valueField = 'name' } = {}) {
  try {
    const params = status ? { status } : undefined;
    const response = await apiGet('/hr/employees', params);
    return mapHrEmployeesToSelectOptions(response, { valueField });
  } catch (error) {
    console.error('Error fetching employees:', error);
    return [];
  }
}

/** Live HR employee options — refreshes when employees change in HR module. */
export function usePointageEmployeeOptions(
  apiGet = localApiAdapter.get.bind(localApiAdapter),
  options = {},
) {
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const refresh = useCallback(async () => {
    setEmployeeOptions(await fetchPointageEmployeeOptions(apiGet, options));
  }, [apiGet, options.status, options.valueField]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useHrApiRefreshListener(HR_EMPLOYEES_REFRESH_EVENT, refresh);

  return { employeeOptions, refresh };
}

/** Resolve employee_id + display name from an id-based CustomSelect value. */
export function resolvePointageEmployeeFromSelect(value, employeeOptions = []) {
  const str = value != null ? String(value).trim() : '';
  if (!str) return { employeeId: null, employeeName: '' };
  const opt = employeeOptions.find((o) => String(o.value) === str);
  if (opt) {
    return {
      employeeId: opt.meta?.id ?? opt.value,
      employeeName: opt.meta?.name || opt.label || str,
    };
  }
  return { employeeId: null, employeeName: str };
}

/** Live HR display name — falls back to stored name when id is missing. */
export function resolvePointageEmployeeDisplayName(employeeId, employeeOptions = [], fallback = '') {
  if (employeeId == null || employeeId === '') return fallback || '—';
  const id = String(employeeId);
  const opt = employeeOptions.find(
    (o) => String(o.value) === id || String(o.meta?.id) === id,
  );
  return opt?.meta?.name || fallback || '—';
}

/** Salary map keyed by HR employee id (overtime hourly-rate lookup). */
export function buildPointageEmployeeSalaryMap(employeeOptions = []) {
  const map = {};
  employeeOptions.forEach((opt) => {
    const id = String(opt.meta?.id ?? opt.value);
    if (id) map[id] = Number(opt.meta?.salary ?? 0);
  });
  return map;
}

/** CustomSelect value for edit forms — prefers id, falls back to name match for legacy rows. */
export function resolvePointageEmployeeSelectValue(record = {}, employeeOptions = []) {
  const id = record.employeeId ?? record.employee_id;
  if (id != null && id !== '') return String(id);
  const name = record.employeeName ?? record.employee_name;
  if (name) {
    const byName = employeeOptions.find(
      (o) => o.meta?.name === name || String(o.label || '').includes(name),
    );
    if (byName) return String(byName.value);
  }
  return '';
}

export function computePointageRowSelection(filteredIds = [], selectedRecords = []) {
  const allRowsSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;
  return { allRowsSelected, someRowsSelected };
}

export function PointageTableSelectHeader({
  allRowsSelected,
  someRowsSelected,
  onSelectAll,
  selectAllTitle,
  deselectAllTitle,
}) {
  return (
    <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
      <div className="flex justify-center">
        <QawaemTableCheckbox
          size="lg"
          checked={allRowsSelected}
          indeterminate={someRowsSelected}
          onChange={onSelectAll}
          title={allRowsSelected ? deselectAllTitle : selectAllTitle}
        />
      </div>
    </th>
  );
}

export function PointageTableSelectCell({ checked, onChange, disabled = false }) {
  return (
    <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
      <div className="flex justify-center">
        <QawaemTableCheckbox checked={checked} onChange={onChange} disabled={disabled} />
      </div>
    </td>
  );
}

/** Map pointage stat config to FacturationListPageLayout stats (max 4). */
export function mapPointageStatCards(entries = [], max = 4) {
  return sliceQawaemStats(entries, max).map((stat, i) => ({
    key: stat.key || stat.label || stat.title || i,
    label: stat.label || stat.title,
    value: stat.value,
    icon: stat.icon,
    variant: stat.variant || (i % 2 === 0 ? 'copper' : 'forest'),
    color: stat.color,
    className: stat.className,
  }));
}

/** Build column picker defs from { key, labelKey? } + label fn `p`. */
export function buildPointageColumnDefs(columns, p) {
  return columns.map((col) => ({
    key: col.key,
    label: col.labelKey ? p(col.labelKey) : col.label,
    checked: col.checked !== false,
    required: col.required,
  }));
}

/** Initial visibility map for table columns (excludes actions). */
export function buildPointageColumnVisibilityState(columns = []) {
  return Object.fromEntries(
    columns
      .filter((c) => c.key !== 'actions')
      .map((c) => [c.key, true]),
  );
}

/** Column picker defs bound to visibility state (for QawaemTableHeaderActionsCell). */
export function buildPointageQawaemColumnDefs(columns, p, columnVisibility) {
  return buildPointageColumnDefs(
    columns.map((col) => ({
      ...col,
      checked: col.key === 'actions' ? true : columnVisibility[col.key] !== false,
    })),
    p,
  );
}

export function isPointageColumnVisible(columnVisibility, key) {
  return columnVisibility[key] !== false;
}

/** Alias for Products/facturation `showCol` pattern. */
export function showPointageCol(columnVisibility, key) {
  return isPointageColumnVisible(columnVisibility, key);
}

/** Guarded column toggle — matches useQawaemTableColumnVisibility. */
export function createPointageHandleToggleColumn(setColumnVisibility, columns = []) {
  return (key) => {
    const col = columns.find((c) => c.key === key);
    if (col?.required || key === 'actions') return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
}

/** Column visibility hook for pointage list tables (QawaemTableHeaderActionsCell). */
export function usePointageTableColumnVisibility(columns = [], p, t = null) {
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(columns),
  );

  const handleToggleColumn = useCallback(
    createPointageHandleToggleColumn(setColumnVisibility, columns),
    [columns],
  );

  const isColVisible = useCallback(
    (key) => isPointageColumnVisible(columnVisibility, key),
    [columnVisibility],
  );
  const showCol = isColVisible;

  const qawaemColumnDefs = useMemo(() => {
    const defs = t
      ? columns.map((col) => {
          if (col.key === 'actions') return { ...col, label: t('common.actions') };
          if (col.labelKey) return col;
          return col;
        })
      : columns;
    return buildPointageQawaemColumnDefs(defs, p, columnVisibility);
  }, [columns, p, t, columnVisibility]);

  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return {
    columnVisibility,
    setColumnVisibility,
    handleToggleColumn,
    isColVisible,
    showCol,
    qawaemColumnDefs,
    qawaemVisibleColumnCount,
    tableColSpan,
  };
}

/** Checkbox column + visible data columns + actions column. */
export function pointageTableColSpan(columnVisibility) {
  const visibleData = Object.values(columnVisibility).filter((v) => v !== false).length;
  return 1 + visibleData + 1;
}

/** Period dropdown labels from page `p()` helper. */
export function buildPointagePeriodLabels(p, t) {
  return {
    label: p('period', { defaultValue: t('common.period', { defaultValue: 'الفترة' }) }),
    startLabel: p('start_date', { defaultValue: t('common.start_date', { defaultValue: 'تاريخ البداية' }) }),
    endLabel: p('end_date', { defaultValue: t('common.end_date', { defaultValue: 'تاريخ النهاية' }) }),
    resetLabel: p('reset', { defaultValue: t('common.reset', { defaultValue: 'إعادة تعيين' }) }),
    applyLabel: p('apply', { defaultValue: t('common.apply', { defaultValue: 'تطبيق' }) }),
    selectPlaceholder: p('select', { defaultValue: t('common.select', { defaultValue: 'اختر' }) }),
  };
}

/** Map main-view tab id → status filter value (overtime / planning pages). */
export function pointageStatusFilterFromMainTab(tabId, tabToStatus = {}) {
  if (!tabId || tabId === 'all' || tabId === 'all_schedules') return 'all';
  return tabToStatus[tabId] ?? (['pending', 'approved', 'rejected', 'active', 'completed'].includes(tabId) ? tabId : 'all');
}

/** Build status options from time-tracking codes for daily/monthly filters. */
export function buildPointageTimeCodeStatusOptions(codes = [], labelFn) {
  const list = Array.isArray(codes) ? codes : [];
  return [
    { value: 'all', label: labelFn?.('filter_all', { defaultValue: 'الكل' }) ?? 'الكل' },
    ...list.map((code) => ({
      value: code.code,
      label: labelFn?.(`code_${code.code}`, { defaultValue: code.label || code.code }) ?? (code.label || code.code),
    })),
  ];
}

/** Secondary status filter for title row (الفترة · status · الأعمدة · search). */
export function buildPointageSecondaryFilter({
  value,
  onChange,
  options,
  filterAllValue = 'all',
  filterAllLabel,
  icon = Tag,
}) {
  const normalized = !value || value === filterAllValue ? filterAllValue : value;
  const current = options.find((o) => o.value === normalized);
  return {
    label: current?.label || filterAllLabel || options[0]?.label,
    value: normalized,
    options,
    onChange: (val) => onChange(val === filterAllValue ? filterAllValue : val),
    icon,
  };
}

/** Legacy controls strip — search visible in card view only (FacturationListPageLayout pattern). */
export function buildPointageControlsStrip({
  qawaem,
  viewMode,
  onViewModeChange,
  isDashboardLightMode,
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  cardsTitle,
  tableTitle,
}) {
  if (qawaem) return null;

  return (
    <div className="facturation-list-controls-strip p-4 border-b border-white/10">
      <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
        {shouldShowCardsToolbarSearch(viewMode) ? (
          <div className="relative min-w-0 flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ) : null}
        <FacturationViewModeToggle
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          isLight={isDashboardLightMode}
          cardsTitle={cardsTitle}
          tableTitle={tableTitle}
        />
      </div>
    </div>
  );
}

const pointageImportExportNoop = () => {};

/** Import/export toolbar props — pass `handlers` when the page supports I/O. */
export function buildPointageImportExportProps({
  showImportDropdown,
  setShowImportDropdown,
  showExportDropdown,
  setShowExportDropdown,
  importDropdownRef,
  exportDropdownRef,
  p,
  t,
  importLabel,
  exportLabel,
  generatingLabel,
  isGeneratingPDF = false,
  handlers = {},
}) {
  return {
    importOpen: showImportDropdown,
    exportOpen: showExportDropdown,
    onToggleImport: () => {
      setShowImportDropdown(!showImportDropdown);
      setShowExportDropdown(false);
    },
    onToggleExport: () => {
      setShowExportDropdown(!showExportDropdown);
      setShowImportDropdown(false);
    },
    importRef: importDropdownRef,
    exportRef: exportDropdownRef,
    importLabel: importLabel || p?.('import', { defaultValue: t?.('common.import', { defaultValue: 'استيراد' }) }),
    exportLabel: exportLabel || t?.('common.export', { defaultValue: 'تصدير' }),
    generatingLabel: generatingLabel || p?.('generating', { defaultValue: t?.('common.loading', { defaultValue: 'جاري الإنشاء...' }) }),
    isGeneratingPDF,
    onImportCSV: handlers.onImportCSV || handlers.onImport || pointageImportExportNoop,
    onImportExcel: handlers.onImportExcel || handlers.onImport || pointageImportExportNoop,
    onImportJSON: handlers.onImportJSON || pointageImportExportNoop,
    onExportPDF: handlers.onExportPDF || pointageImportExportNoop,
    onExportExcel: handlers.onExportExcel || pointageImportExportNoop,
    onExportCSV: handlers.onExportCSV || handlers.onExport || pointageImportExportNoop,
    onExportJSON: handlers.onExportJSON || pointageImportExportNoop,
    showJsonImportExport: handlers.showJsonImportExport ?? false,
  };
}

/** Lock body scroll while any pointage modal is open. */
export function usePointageModalBodyLock(...openFlags) {
  const isOpen = openFlags.some(Boolean);
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);
}

/**
 * Tab layout tier from editable field count (Products.js audit rule).
 * ≤8 fields → no tabs | 9–17 → two tabs | ≥18 → two or three tabs.
 */
export function getPointageFormTabTier(fieldCount) {
  if (fieldCount <= 8) return 'none';
  if (fieldCount <= 17) return 'two';
  return 'three';
}

/** Build tabs array for tier; pass { two: [...], three?: [...] }. Returns [] when no tabs. */
export function buildPointageFormTabs(tabsByTier, fieldCount) {
  const tier = getPointageFormTabTier(fieldCount);
  if (tier === 'none') return [];
  return tabsByTier[tier] || tabsByTier.two || [];
}

/** True when tab panel should render (no tabs = always visible). */
export function pointageFormTabVisible(tabs, activeTab, panelId) {
  if (!tabs?.length) return true;
  return activeTab === panelId;
}

/** Theme-aware class tokens for modal form sections (light/dark). */
export function getPointageModalFormClasses(isLight) {
  return {
    section: isLight
      ? 'rounded-xl border border-slate-200 bg-white overflow-hidden'
      : 'rounded-xl border border-white/20 bg-white/5 overflow-hidden',
    sectionBtn: isLight
      ? 'w-full flex items-center justify-between p-4 transition-colors hover:bg-slate-50'
      : 'w-full flex items-center justify-between p-4 transition-colors hover:bg-white/5',
    sectionTitle: isLight ? 'text-lg font-semibold text-slate-900' : 'text-lg font-semibold text-white',
    label: isLight ? 'block w-full text-slate-700 text-sm mb-2 font-medium text-right' : 'block w-full text-white/70 text-sm mb-2 font-medium text-right',
    chevron: isLight ? 'text-slate-500' : 'text-white/60',
    viewCard: isLight
      ? 'rounded-xl p-5 border border-slate-200 bg-slate-50'
      : 'rounded-xl p-5 border border-white/20 bg-white/5',
    viewHeading: isLight ? 'text-slate-900 font-semibold mb-4' : 'text-white font-semibold mb-4',
    viewLabel: isLight ? 'text-slate-500 text-sm mb-1' : 'text-white/60 text-sm mb-1',
    viewValue: isLight ? 'text-slate-900 font-medium' : 'text-white font-medium',
    textarea: isLight
      ? 'w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all'
      : 'w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all',
  };
}
